import { formatCurrency } from '../../../utils/Masks';
import type {
  NosisCheckRow,
  NosisCountAmount,
  NosisFinePayments,
  NosisMonthlyEvolution,
  NosisPjSection,
  NosisRedNosis,
  NosisRejectedChecksDetail,
  NosisRejectionHistory,
} from '../../../types/interfaces';

export const PJ_BAND_KEYS = ['6M', '7A12M', '13A24M'] as const;
export type PjBandKey = (typeof PJ_BAND_KEYS)[number];

export const PJ_BAND_LABELS: Record<PjBandKey, string> = {
  '6M': '≤ 6 m',
  '7A12M': '7 a 12 m',
  '13A24M': '13 a 24 m',
};

export type AntecedentesBlock = {
  id: string;
  titleLabel: string;
  titleEmphasis?: string;
  primaryValue: string;
  secondaryLine: string | null;
  emphasize: boolean;
  missingVariable?: boolean;
};

export function formatNosisCount(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-';
  return String(value);
}

export function formatNosisAmount(
  value: number | string | null | undefined
): string {
  if (value === null || value === undefined || value === '') return '-';
  const n = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(n)) return '-';
  return `$ ${formatCurrency(Math.round(n), true)}`;
}

export function formatNosisDate(
  raw: string | null | undefined,
  style: 'full' | 'short' = 'full'
): string {
  if (raw == null || raw === '') return '-';
  const s = String(raw).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const [, y, m, d] = iso;
    return style === 'short' ? `${d}/${m}/${y.slice(2)}` : `${d}/${m}/${y}`;
  }
  const dmy = s.match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    if (style === 'short' && y.length === 4) return `${d}/${m}/${y.slice(2)}`;
    return `${d}/${m}/${y}`;
  }
  return s;
}

export function formatReasonLabel(reason: string | null | undefined): string {
  if (reason == null || reason === '') return '-';
  const trimmed = reason.trim();
  if (
    /^SF\b/i.test(trimmed) ||
    /SinFondos/i.test(trimmed) ||
    /^2\s*-\s*SinFondos/i.test(trimmed)
  ) {
    return 'Sin fondos';
  }
  if (trimmed === 'SF') return 'Sin fondos';
  return trimmed;
}

export function formatCheckEstado(paymentDate: string | null | undefined): {
  kind: 'impago' | 'date';
  label: string;
} {
  if (paymentDate == null || paymentDate === '') {
    return { kind: 'impago', label: 'IMPAGO' };
  }
  return { kind: 'date', label: formatNosisDate(paymentDate, 'full') };
}

export function formatCheckDias(
  daysToPayment: number | null | undefined
): string {
  if (daysToPayment === null || daysToPayment === undefined) return '-';
  return String(daysToPayment);
}

export function formatCheckMulta(
  finePaid: string | null | undefined,
  finePaymentDate: string | null | undefined
): string {
  if (finePaid == null || String(finePaid).trim() === '') return '-';
  const flag = String(finePaid).trim().toUpperCase();
  if (flag === 'SI' || flag === 'SÍ') {
    if (finePaymentDate)
      return `Paga ${formatNosisDate(finePaymentDate, 'short')}`;
    return 'Paga';
  }
  if (flag === 'NO') return 'Impaga';
  return '-';
}

export function formatCountAmount(pair: NosisCountAmount | null | undefined): {
  count: string;
  amount: string;
} {
  if (!pair) return { count: '-', amount: '-' };
  return {
    count: formatNosisCount(pair.count),
    amount: formatNosisAmount(pair.amount),
  };
}

export function buildAntecedentesBlocks(
  detail: NosisRejectedChecksDetail | null | undefined
): AntecedentesBlock[] {
  const status = detail?.detailStatus;
  const history: NosisRejectionHistory | null | undefined =
    detail?.rejectionHistory;
  const last =
    history?.lastRejectionDate != null
      ? `último ${formatNosisDate(history.lastRejectionDate, 'full')}`
      : null;

  const emptyPair = (): { count: string; amount: string } =>
    status === 'NO_DATA'
      ? { count: '-', amount: '-' }
      : { count: '0', amount: '$ 0' };

  const sfNoPag = history
    ? formatCountAmount(history.insufficientFunds.unpaid)
    : emptyPair();
  const sfPag = history
    ? formatCountAmount(history.insufficientFunds.paid)
    : emptyPair();
  const sfMulta = history
    ? formatCountAmount(history.insufficientFunds.finePending)
    : emptyPair();
  const otNoPag = history
    ? formatCountAmount(history.otherReasons.unpaid)
    : emptyPair();

  const noPagCount = history?.insufficientFunds.unpaid.count ?? 0;
  const emphasize = status === 'REJECTED' && (noPagCount ?? 0) > 0;
  const otMissing =
    status !== 'NO_DATA' &&
    history?.otherReasons?.unpaid?.count == null &&
    history?.otherReasons?.unpaid?.amount == null;

  const sfNoPagSecondary =
    emphasize && sfNoPag.count !== '-'
      ? `${formatChequeCountLabel(sfNoPag.count)} · todos sin fondos${last ? ` · ${last}` : ''}`
      : null;

  return [
    {
      id: 'sf-nopag',
      titleLabel: 'Rechazado impago',
      primaryValue: sfNoPag.amount,
      secondaryLine: sfNoPagSecondary,
      emphasize,
    },
    {
      id: 'sf-pag',
      titleLabel: 'Regularizado',
      primaryValue: sfPag.count,
      secondaryLine: sfPag.amount !== '-' ? sfPag.amount : null,
      emphasize: false,
    },
    {
      id: 'sf-multa',
      titleLabel: 'Sin fondos /',
      titleEmphasis: 'Multa impaga',
      primaryValue: sfMulta.count,
      secondaryLine: sfMulta.amount !== '-' ? sfMulta.amount : null,
      emphasize: false,
    },
    {
      id: 'ot-nopag',
      titleLabel: 'Otros motivos /',
      titleEmphasis: 'No pagados',
      primaryValue: otMissing ? 'Sin dato' : otNoPag.count,
      secondaryLine: otMissing
        ? 'Variable no recibida'
        : otNoPag.amount !== '-'
          ? otNoPag.amount
          : null,
      emphasize: false,
      missingVariable: otMissing,
    },
  ];
}

export function formatMonthlyCell(
  available: boolean,
  count: number | null | undefined,
  amount: number | null | undefined,
  kind: 'count' | 'amount'
): string {
  if (!available) return '-';
  return kind === 'count' ? formatNosisCount(count) : formatNosisAmount(amount);
}

export function formatFineRow(
  finePayments: NosisFinePayments | null | undefined
): Array<{ estado: string; cantidad: string; desde: string; hasta: string }> {
  const paid = finePayments?.paid;
  const unpaid = finePayments?.unpaid;
  return [
    {
      estado: 'Pagas',
      cantidad: formatNosisCount(paid?.count ?? 0),
      desde: paid?.from ? formatNosisDate(paid.from, 'full') : '-',
      hasta: paid?.to ? formatNosisDate(paid.to, 'full') : '-',
    },
    {
      estado: 'Impagas',
      cantidad: formatNosisCount(unpaid?.count ?? 0),
      desde: '-',
      hasta: '-',
    },
  ];
}

export type RedNosisMetric = {
  id: string;
  periodLabel: string;
  titleLabel: string;
  primary: string;
  secondary?: string;
};

export function buildRedNosisMetrics(
  red: NosisRedNosis | null | undefined
): RedNosisMetric[] {
  const cant = (v: number | null | undefined) => formatNosisCount(v);
  const mon = (v: number | null | undefined) => formatNosisAmount(v);

  return [
    {
      id: 'pos-6',
      periodLabel: 'Últ. 6 meses',
      titleLabel: 'Positivos',
      primary: cant(red?.positive?.m6Count),
      secondary: mon(red?.positive?.m6Amount),
    },
    {
      id: 'pos-7-12',
      periodLabel: '7 a 12 meses',
      titleLabel: 'Positivos',
      primary: cant(red?.positive?.m7to12Count),
      secondary: mon(red?.positive?.m7to12Amount),
    },
    {
      id: 'pos-13-24',
      periodLabel: '13 a 24 meses',
      titleLabel: 'Positivos',
      primary: cant(red?.positive?.m13to24Count),
      secondary: mon(red?.positive?.m13to24Amount),
    },
    {
      id: 'pend-dep',
      periodLabel: '',
      titleLabel: 'Pendientes de depósito',
      primary: cant(red?.pendingDeposit6m?.count),
      secondary: mon(red?.pendingDeposit6m?.amount),
    },
    {
      id: 'redondo-12',
      periodLabel: 'Últ. 12 meses',
      titleLabel: 'Importe redondo 6m',
      primary: cant(red?.roundAmounts?.m12),
    },
    {
      id: 'inf-12',
      periodLabel: 'Últ. 12 meses',
      titleLabel: 'Informados',
      primary: cant(red?.reported?.m12),
    },
  ];
}

export function pjBandCell(
  pj: NosisPjSection | null | undefined,
  reason: string,
  band: PjBandKey,
  field: 'count' | 'amount'
): string {
  const metrics = pj?.rows.find((r) => r.reason === reason)?.bands?.[band];
  if (!metrics) return '-';
  return field === 'count'
    ? formatNosisCount(metrics.totalCount)
    : formatNosisAmount(metrics.totalAmount);
}

export function pjTotalsCell(
  pj: NosisPjSection | null | undefined,
  band: PjBandKey,
  kind: 'rejected' | 'paid' | 'pct',
  field: 'count' | 'amount' = 'count'
): string {
  const t = pj?.totals?.[band];
  if (!t) return '-';
  if (kind === 'pct') {
    if (field === 'amount') {
      const rejectedAmount = Number(t.rejected.amount);
      if (!Number.isFinite(rejectedAmount) || rejectedAmount === 0) return '-';
      const paidAmount = Number(t.paid.amount);
      if (!Number.isFinite(paidAmount)) return '-';
      const pct = (paidAmount / rejectedAmount) * 100;
      return `${formatCurrency(Math.round(pct), true)} %`;
    }
    if (t.paidPct == null) return '-';
    return `${formatCurrency(Math.round(Number(t.paidPct)), true)} %`;
  }
  const pair = kind === 'rejected' ? t.rejected : t.paid;
  return field === 'count'
    ? formatNosisCount(pair.count)
    : formatNosisAmount(pair.amount);
}

export const PJ_DEFECTOS_FORMALES = 'Defectos formales';

export function ensurePjReasonRows(
  rows: NosisPjSection['rows'] | null | undefined
): NosisPjSection['rows'] {
  const list = [...(rows ?? [])];
  const hasFormal = list.some(
    (r) => formatReasonLabel(r.reason) === PJ_DEFECTOS_FORMALES
  );
  if (!hasFormal) {
    list.push({ reason: PJ_DEFECTOS_FORMALES, bands: {} });
  }
  return list;
}

export function mapCheckRowsForTable(
  checks: NosisCheckRow[] | null | undefined
) {
  return (checks ?? []).map((row, index) => {
    const estado = formatCheckEstado(row.paymentDate);
    return {
      id: `${row.checkNumber}-${row.rejectionDate}-${index}`,
      rejectionDate: formatNosisDate(row.rejectionDate, 'full'),
      checkNumber: row.checkNumber || '-',
      amount: formatNosisAmount(row.amount),
      reason: formatReasonLabel(row.reason),
      statusKind: estado.kind,
      statusLabel: estado.label,
      daysToPayment: formatCheckDias(row.daysToPayment),
      fine: formatCheckMulta(row.finePaid, row.finePaymentDate),
      relatedCuit: row.relatedCuit?.trim() ?? '',
    };
  });
}

export type MonthlyRowView = {
  id: string;
  periodo: string;
  rejCant: string;
  rejMonto: string;
  pagCant: string;
  pagMonto: string;
  pendCant: string;
  pendMonto: string;
  available: boolean;
};

export function mapMonthlyRows(
  months: NosisMonthlyEvolution[] | null | undefined
): MonthlyRowView[] {
  return (months ?? []).map((m) => ({
    id: m.yearMonth,
    periodo: m.yearMonth,
    rejCant: formatMonthlyCell(
      m.available,
      m.rejected.count,
      m.rejected.amount,
      'count'
    ),
    rejMonto: formatMonthlyCell(
      m.available,
      m.rejected.count,
      m.rejected.amount,
      'amount'
    ),
    pagCant: formatMonthlyCell(
      m.available,
      m.paid.count,
      m.paid.amount,
      'count'
    ),
    pagMonto: formatMonthlyCell(
      m.available,
      m.paid.count,
      m.paid.amount,
      'amount'
    ),
    pendCant: formatMonthlyCell(
      m.available,
      m.pendingCumulative.count,
      m.pendingCumulative.amount,
      'count'
    ),
    pendMonto: formatMonthlyCell(
      m.available,
      m.pendingCumulative.count,
      m.pendingCumulative.amount,
      'amount'
    ),
    available: m.available,
  }));
}

export function buildMonthlyTotalActual(
  months: NosisMonthlyEvolution[] | null | undefined
): MonthlyRowView {
  const available = (months ?? []).filter((m) => m.available);

  const sumCount = (
    pick: (m: NosisMonthlyEvolution) => number | null | undefined
  ): number | null => {
    let sum = 0;
    let any = false;
    for (const m of available) {
      const v = pick(m);
      if (v === null || v === undefined) continue;
      sum += v;
      any = true;
    }
    return any ? sum : null;
  };

  const sumAmount = (
    pick: (m: NosisMonthlyEvolution) => number | null | undefined
  ): number | null => {
    let sum = 0;
    let any = false;
    for (const m of available) {
      const v = pick(m);
      if (v === null || v === undefined) continue;
      sum += Number(v);
      any = true;
    }
    return any ? sum : null;
  };

  const hasAny = available.length > 0;
  const last = hasAny ? available[available.length - 1] : null;
  return {
    id: 'total-actual',
    periodo: 'Total actual',
    rejCant: hasAny ? formatNosisCount(sumCount((m) => m.rejected.count)) : '-',
    rejMonto: hasAny
      ? formatNosisAmount(sumAmount((m) => m.rejected.amount))
      : '-',
    pagCant: hasAny ? formatNosisCount(sumCount((m) => m.paid.count)) : '-',
    pagMonto: hasAny ? formatNosisAmount(sumAmount((m) => m.paid.amount)) : '-',
    pendCant: hasAny ? formatNosisCount(last?.pendingCumulative.count) : '-',
    pendMonto: hasAny ? formatNosisAmount(last?.pendingCumulative.amount) : '-',
    available: hasAny,
  };
}

export function formatChequeCountLabel(count: string): string {
  if (count === '-' || count === '0') return count;
  return count === '1' ? `${count} cheque` : `${count} cheques`;
}

export function isNosisRejectedChecksSectionVisible(
  detailEnabled: boolean | undefined,
  uiEnabled: boolean | undefined
): boolean {
  return detailEnabled === true && uiEnabled === true;
}
